import { useQuery } from "@tanstack/react-query";
import { useAxios } from "@/app/providers/AxiosProvider";
import handleError from "@lib/helpers/handleError";
import { useCallback, useEffect, useState } from "react";
import useRecruiterDetails from "./useRecruiterDetails";

// Dismissed notifications are hidden client-side and persisted so they stay
// hidden across refetches (initial load + Echo pushes). The API only exposes a
// "read" flag, and `recent` still returns read notifications, so a local set is
// needed to actually hide them.
const DISMISSED_STORAGE_KEY = "dismissedNotifications";

function readDismissed() {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(DISMISSED_STORAGE_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Ignore write failures (private mode, quota, etc.) — hiding still works
    // for the current session via in-memory state.
  }
}

function useFetch() {
  const api = useAxios();
  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications/recent"),
    enabled: false,
  });

  handleError(error);
  const notifications = data?.data ?? [];

  return { notifications, isFetching, refresh: refetch };
}

export default function useNotifications() {
  const { notifications: allNotifications, isFetching, refresh } = useFetch();
  const { recruiter, isLoading: isRecruiterLoading } = useRecruiterDetails();
  const api = useAxios();

  const [dismissedIds, setDismissedIds] = useState(readDismissed);

  const echo = typeof window === "undefined" ? {} : window.Echo;

  useEffect(() => {
    if (isRecruiterLoading) return;

    // initial load
    refresh();

    // Setup Echo listener only if Echo is available (WebSocket support)
    if (typeof window !== "undefined" && typeof window.Echo !== "undefined") {
      window.Echo.private(`app.user.${recruiter?.user?.id || ""}`).listen(
        "NotificationCreated",
        (e) => {
          refresh();
        }
      );
    }
  }, [isRecruiterLoading, echo]);

  const dismiss = useCallback(
    (id) => {
      if (id == null) return;
      // Prune ids the server no longer returns while adding the new one, so the
      // persisted set stays bounded to the current notification window.
      const liveIds = new Set(allNotifications.map((n) => n.id));
      setDismissedIds((prev) => {
        const next = [...new Set([...prev.filter((d) => liveIds.has(d)), id])];
        writeDismissed(next);
        return next;
      });
      // Best-effort: also mark it read server-side so a dismissed notification
      // doesn't resurface as unread elsewhere (e.g. the header bell badge).
      api.post(`/notifications/read`, { notifications: [id] }).catch(() => {});
    },
    [api, allNotifications]
  );

  const dismissedSet = new Set(dismissedIds);
  const notifications = allNotifications.filter((n) => !dismissedSet.has(n.id));

  return { notifications, isFetching, dismiss };
}
